'use client';
import React from 'react';

const Footer: React.FC = () => {
  const handleScrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const navLinks = [
    { label: 'Home', href: 'home' },
    { label: 'About', href: 'about' },
    { label: 'Publishing', href: 'publishing' },
    { label: 'Contact', href: 'contact' },
  ];

  const publishingLinks = [
    'Magazine Publishing',
    'Editorial Content',
    'Digital Publications',
    'Periodical Content',
    'Publishing Services',
    'Media & Content Development',
  ];

  return (
    <footer
      style={{ backgroundColor: '#122658' }}
      aria-label="Site footer"
    >
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#0066CC' }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M4 4h16v2H4V4zm0 4h16v2H4V8zm0 4h10v2H4v-2zm0 4h16v2H4v-2z" fill="white"/>
                  <circle cx="18" cy="14" r="4" fill="#387EF7"/>
                  <path d="M17 13l1 1 2-2" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div>
                <p style={{ fontFamily: 'Roboto, sans-serif', fontWeight: 700, fontSize: '16px', color: '#ffffff', lineHeight: 1.2 }}>Caps N Corks II</p>
                <p style={{ fontFamily: 'Poppins, sans-serif', fontSize: '11px', color: 'rgba(255,255,255,0.5)', letterSpacing: '1px' }}>LLC</p>
              </div>
            </div>
            <p style={{ fontFamily: 'Poppins, sans-serif', fontSize: '13px', color: 'rgba(255,255,255,0.65)', lineHeight: 1.8, marginBottom: '20px' }}>
              Professional magazine and periodicals publishing company based in Jacksonville, Florida.
            </p>
            <div className="space-y-3">
              <div className="flex items-start gap-2">
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" className="mt-0.5 flex-shrink-0">
                  <path d="M7 1C4.79 1 3 2.79 3 5c0 2.92 4 8 4 8s4-5.08 4-8c0-2.21-1.79-4-4-4zm0 5.5A1.5 1.5 0 115.5 5 1.5 1.5 0 017 6.5z" fill="rgba(255,255,255,0.5)"/>
                </svg>
                <p style={{ fontFamily: 'Poppins, sans-serif', fontSize: '12px', color: 'rgba(255,255,255,0.6)', lineHeight: 1.6 }}>
                  2974 Montila Drive<br />Jacksonville, FL 32246, USA
                </p>
              </div>
              <div className="flex items-center gap-2">
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" className="flex-shrink-0">
                  <path d="M2.1 4.55c.82 1.64 2.22 2.98 3.86 3.86L7.25 7.12c.17-.17.41-.24.59-.11.64.24 1.34.35 2.1.35.35 0 .59.24.59.59v2.92c0 .35-.24.59-.59.59C5.02 11.46 0 6.44 0 .59 0 .24.24 0 .59 0H3.5c.35 0 .59.24.59.59 0 .76.11 1.46.35 2.1.11.17.06.41-.11.59L2.1 4.55z" fill="rgba(255,255,255,0.5)"/>
                </svg>
                <a href="tel:+14083873442" style={{ fontFamily: 'Poppins, sans-serif', fontSize: '12px', color: 'rgba(255,255,255,0.6)', textDecoration: 'none' }}>
                  (408) 387-3442
                </a>
              </div>
              <div className="flex items-center gap-2">
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" className="flex-shrink-0">
                  <path d="M12.5 2h-11C.67 2 0 2.67 0 3.5v7c0 .83.67 1.5 1.5 1.5h11c.83 0 1.5-.67 1.5-1.5v-7C14 2.67 13.33 2 12.5 2zm0 2.5L7 8 1.5 4.5V3l5.5 3.5L12.5 3v1.5z" fill="rgba(255,255,255,0.5)"/>
                </svg>
                <a href="mailto:support@capsnicorks.com" style={{ fontFamily: 'Poppins, sans-serif', fontSize: '11px', color: 'rgba(255,255,255,0.6)', textDecoration: 'none', wordBreak: 'break-all' }}>
                  support@capsnicorks.com
                </a>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <div>
            <h5 style={{ fontFamily: 'Roboto, sans-serif', fontWeight: 700, fontSize: '14px', color: '#ffffff', marginBottom: '20px', letterSpacing: '1px', textTransform: 'uppercase' }}>
              Navigation
            </h5>
            <ul className="space-y-3">
              {navLinks.map((link) => (
                <li key={link.label}>
                  <button
                    onClick={() => handleScrollTo(link.href)}
                    style={{ fontFamily: 'Poppins, sans-serif', fontSize: '13px', color: 'rgba(255,255,255,0.65)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, transition: 'color 0.2s' }}
                    className="hover:text-white"
                  >
                    {link.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Publishing */}
          <div>
            <h5 style={{ fontFamily: 'Roboto, sans-serif', fontWeight: 700, fontSize: '14px', color: '#ffffff', marginBottom: '20px', letterSpacing: '1px', textTransform: 'uppercase' }}>
              Publishing
            </h5>
            <ul className="space-y-3">
              {publishingLinks.map((link) => (
                <li key={link}>
                  <button
                    onClick={() => handleScrollTo('publishing')}
                    style={{ fontFamily: 'Poppins, sans-serif', fontSize: '13px', color: 'rgba(255,255,255,0.65)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, transition: 'color 0.2s', textAlign: 'left' }}
                    className="hover:text-white"
                  >
                    {link}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* CTA Column */}
          <div>
            <h5 style={{ fontFamily: 'Roboto, sans-serif', fontWeight: 700, fontSize: '14px', color: '#ffffff', marginBottom: '20px', letterSpacing: '1px', textTransform: 'uppercase' }}>
              Get in Touch
            </h5>
            <p style={{ fontFamily: 'Poppins, sans-serif', fontSize: '13px', color: 'rgba(255,255,255,0.65)', lineHeight: 1.7, marginBottom: '16px' }}>
              Have a question or want to discuss a publishing project? We would love to hear from you.
            </p>
            <button
              onClick={() => handleScrollTo('contact')}
              className="inline-flex items-center justify-center px-6 py-3 text-white font-medium uppercase tracking-widest transition-all duration-200 hover:opacity-90 mb-4"
              style={{
                backgroundColor: '#0066CC',
                borderRadius: '7px',
                fontFamily: 'Poppins, sans-serif',
                fontSize: '12px',
                letterSpacing: '2px',
                display: 'block',
                width: '100%',
                textAlign: 'center',
              }}
            >
              Request a Callback
            </button>
            <a
              href="tel:+14083873442"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 font-medium uppercase tracking-widest transition-all duration-200 hover:bg-white hover:text-navy-DEFAULT"
              style={{
                backgroundColor: 'transparent',
                border: '1px solid rgba(255,255,255,0.3)',
                borderRadius: '7px',
                fontFamily: 'Poppins, sans-serif',
                fontSize: '12px',
                letterSpacing: '2px',
                color: 'rgba(255,255,255,0.8)',
                textDecoration: 'none',
                display: 'block',
                textAlign: 'center',
              }}
              aria-label="Call Caps N Corks II"
            >
              Call Us Now
            </a>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div
        className="border-t"
        style={{ borderColor: 'rgba(255,255,255,0.1)' }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <p style={{ fontFamily: 'Poppins, sans-serif', fontSize: '12px', color: 'rgba(255,255,255,0.5)' }}>
              &copy; {new Date().getFullYear()} Caps N Corks II, LLC. All rights reserved.
            </p>
            <div className="flex items-center gap-6">
              <button
                onClick={() => handleScrollTo('home')}
                style={{ fontFamily: 'Poppins, sans-serif', fontSize: '12px', color: 'rgba(255,255,255,0.5)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                className="hover:text-white transition-colors"
              >
                Privacy Policy
              </button>
              <button
                onClick={() => handleScrollTo('home')}
                style={{ fontFamily: 'Poppins, sans-serif', fontSize: '12px', color: 'rgba(255,255,255,0.5)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                className="hover:text-white transition-colors"
              >
                Terms of Use
              </button>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
